import React, { useCallback, useEffect, useState } from 'react';
import { Table } from '@tanstack/react-table';
import { Plus, Users } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import ConfirmationModal from '../../components/blocks/confirmation-modal/confirmation-modal';
import { useForgotPassword, useResendActivation } from 'features/auth/hooks/use-auth';
import { useGetUsersQuery, useDeactivateUser } from 'features/iam/hooks/use-iam';
import { useCancelAllUserSubscriptions } from 'features/subscription/hooks/use-subscription';
import { createIamTableColumns } from 'features/iam/components/iam-table/iam-table-columns';
import { IamTableToolbar } from 'features/iam/components/iam-table/iam-table-toolbar';
import { IamData } from 'features/iam/services/user-service';
import { useIsMobile } from '../../hooks/use-mobile';
import ExpandedUserDetails from 'features/iam/components/user-details-mobile-view/expanded-user-details';
import DataTable from '../../components/blocks/data-table/data-table';
import { Button } from 'components/ui/button';
import { Dialog, DialogTrigger } from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { AddUser } from 'features/iam/components/add-profile/add-profile';
import { UserDetails, AddMultipleUsers, ChangePlanDialog } from 'features/iam';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from "state/store/common/useCommonStore";
import { Navigate } from "react-router-dom";
import { useToast } from '../../hooks/use-toast';
import GroupsDataTable from 'features/iam/components/iam-table/iam-group-table';
import { AddGroupModal } from 'features/iam/components/iam-table/add-group-modal';

interface PaginationState {
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

// Map URL parameters to internal tab IDs
const urlParamToTabId: Record<string, string> = {
  'all-users': 'allUsers',
  'groups': 'groups'
};

const tabIdToUrlParam: Record<string, string> = {
  'allUsers': 'all-users',
  'groups': 'groups'
};

/**
 * Table toolbar component for handling filters and search.
 */
const TableToolbar = ({
  table,
  onSearch,
  columns,
}: {
  table: Table<IamData>;
  onSearch: (filters: { email: string; name: string; organizationIds: string[] }) => void;
  columns: any[];
}) => {
  return <IamTableToolbar table={table} onSearch={onSearch} columns={columns} />;
};

/**
 * Main IAM (Identity Access Management) Table Page.
 * Renders user data in a table with search, pagination, row expansion, and modals.
 */
export function IamTablePage() {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabId, setTabId] = useState('allUsers');
  const [openSheet, setOpenSheet] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IamData | null>(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isResendActivationModalOpen, setIsResendActivationModalOpen] = useState(false);
  const [isDeactivateUserModalOpen, setIsDeactivateUserModalOpen] = useState(false);
  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] = useState(false);
  const { mutateAsync: resetPassword } = useForgotPassword();
  const { mutateAsync: resendActivation } = useResendActivation();
  const { mutateAsync: deactivateUser } = useDeactivateUser();
  const { mutate: cancelAllUserSubscriptions } = useCancelAllUserSubscriptions({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
      setIsCancelSubscriptionModalOpen(false);
    },
  });
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isAddMultipleUsersModalOpen, setIsAddMultipleUsersModalOpen] = useState(false);
  const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
  const { t } = useTranslation();
  const { toast } = useToast();
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const isSuperAdmin = useCommonStore((state) => state.isSuperAdmin);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [isReloadGroupsData, setReloadGroupsData] = useState(false);

  const [filters, setFilters] = useState({
    email: '',
    name: '',
    organizationIds: [] as string[],
  });

  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
    totalCount: 0,
  });

  // Check if we're in group details view
  const isAddGroupButtonShow = searchParams.get('group') !== null;

  // Initialize tab from URL parameter on component mount
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && urlParamToTabId[tabParam]) {
      setTabId(urlParamToTabId[tabParam]);
    }
  }, [searchParams]);

  // Function to handle tab change and update URL
  const handleTabChange = (newTabId: string) => {
    setTabId(newTabId);
    const urlParam = tabIdToUrlParam[newTabId];
    if (urlParam) {
      setSearchParams({ tab: urlParam });
    }
  };

  const queryParams = {
    page: paginationState.pageIndex,
    pageSize: paginationState.pageSize,
    filter: filters,
    enabled: isAdmin || isOrgAdmin,
  };

  // Fetch users based on query parameters
  const { data, isLoading, error } = useGetUsersQuery(queryParams);

  /**
   * Handles pagination changes.
   */
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPaginationState((prev) => ({
        ...prev,
        pageIndex: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
      }));
    },
    []
  );

  /**
   * Updates total count when new data is available.
   */
  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setPaginationState((prev) => ({
        ...prev,
        totalCount: data.totalCount,
      }));
    }
  }, [data?.totalCount]);

  /**
   * Handles search input changes and resets pagination.
   */
  const handleSearch = useCallback((newFilters: { email: string; name: string; organizationIds: string[] }) => {
    setFilters(newFilters);
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, []);

  /**
   * Disables scrolling when details modal is open.
   */
  useEffect(() => {
    if (openSheet) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [openSheet]);

  if (!isAdmin && !isOrgAdmin) {
    return <Navigate to="/home" />;
  }

  /**
   * Confirms password reset for the selected user.
   */
  const handleConfirmResetPassword = async () => {
    if (!selectedUser) return;
    try {
      await resetPassword({ email: selectedUser.email });
      setIsResetPasswordModalOpen(false);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  /**
   * Confirms activation resend for the selected user.
   */
  const handleConfirmActivation = async () => {
    if (!selectedUser) return;
    try {
      await resendActivation({ userId: selectedUser.itemId });
      setIsResendActivationModalOpen(false);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  /**
   * Confirms deactivation for the selected user.
   */
  const handleConfirmDeactivation = async () => {
    if (!selectedUser) return;
    await deactivateUser(selectedUser.itemId);
    setIsDeactivateUserModalOpen(false);
  };

  /**
   * Confirms subscription cancellation for the selected user.
   */
  const handleConfirmCancelSubscription = () => {
    if (!selectedUser) return;
    cancelAllUserSubscriptions({ userId: selectedUser.itemId });
  };

  /**
   * Opens the user details modal.
   */
  const handleViewDetails = (user: IamData) => {
    setSelectedUser(user);
    setOpenSheet(true);
  };

  /**
   * Opens reset password confirmation modal.
   */
  const handleResetPassword = (user: IamData) => {
    setSelectedUser(user);
    setIsResetPasswordModalOpen(true);
  };

  /**
   * Opens resend activation confirmation modal.
   */
  const handleResendActivation = (user: IamData) => {
    setSelectedUser(user);
    setIsResendActivationModalOpen(true);
  };

  /**
   * Opens deactivate user confirmation modal.
   */
  const handleDeactivateUser = (user: IamData) => {
    setSelectedUser(user);
    setIsDeactivateUserModalOpen(true);
  };

  /**
   * Opens change plan modal.
   */
  const handleChangePlan = (user: IamData) => {
    setSelectedUser(user);
    setIsChangePlanModalOpen(true);
  };

  /**
   * Opens cancel subscription confirmation modal.
   */
  const handleCancelSubscription = (user: IamData) => {
    setSelectedUser(user);
    setIsCancelSubscriptionModalOpen(true);
  };

  /**
   * Fallback copy function for older browsers.
   */
  const fallbackCopyToClipboard = (text: string) => {
    const tempTextArea = document.createElement('textarea');
    tempTextArea.value = text;
    document.body.appendChild(tempTextArea);
    tempTextArea.select();

    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Failed to copy using execCommand: ', err);
    } finally {
      document.body.removeChild(tempTextArea);
    }
  };

  /**
   * Handles copying email to clipboard.
   */
  const handleCopyEmail = (email: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(email).then(() => {
          toast({
            variant: 'success',
            title: t('COPIED'),
            description: t('TEXT_COPY_SUCCESS'),
          });
        });
      } else {
        fallbackCopyToClipboard(email);
        toast({
          variant: 'success',
          title: t('COPIED'),
          description: t('TEXT_COPY_SUCCESS'),
        });
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        variant: 'destructive',
        title: t('FAILED_TO_COPY_TEXT'),
        description: t('FAILED_COPY_TO_CLIPBOARD'),
      });
    }
  };

  // Define table columns with actions
  const columns = createIamTableColumns({
    onViewDetails: handleViewDetails,
    onResetPassword: handleResetPassword,
    onResendActivation: handleResendActivation,
    onDeactivateUser: handleDeactivateUser,
    onChangePlan: isSuperAdmin ? handleChangePlan : undefined,
    onCancelSubscription: isSuperAdmin ? handleCancelSubscription : undefined,
    onCopyEmail: handleCopyEmail,
    t
  });

  // Show error message if data fetching fails
  if (error) {
    return <div className="p-4 text-error">{t('ERROR_LOADING_USERS')}: {error.error?.message}</div>;
  }

  /**
   * Renders expanded row content for mobile view.
   */
  const renderExpandedContent = (user: IamData) => (
    <ExpandedUserDetails
      user={user}
      onResetPassword={handleResetPassword}
      onResendActivation={handleResendActivation}
      onDeactivateUser={handleDeactivateUser}
      onChangePlan={isSuperAdmin ? handleChangePlan : undefined}
      onCancelSubscription={isSuperAdmin ? handleCancelSubscription : undefined}
    />
  );

  /**
   * Renders table filter toolbar.
   */
  const renderFilterToolbar = (table: Table<IamData>) => (
    <TableToolbar table={table} onSearch={handleSearch} columns={columns} />
  );

  const reloadDataGroupsData = () => {
    setReloadGroupsData(!isReloadGroupsData);
  }

  return (
    <div className="flex flex-col h-full w-full">
      <div className="h-full flex-col flex w-full mb-2 md:mb-[18px]">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h2 className="text-2xl font-bold tracking-tight whitespace-nowrap">{t('IDENTITY_ACCESS_MANAGEMENT')}</h2>

          {tabId === 'allUsers' ?
            <div className="flex flex-col sm:flex-row gap-2">
              <Dialog open={isAddMultipleUsersModalOpen} onOpenChange={setIsAddMultipleUsersModalOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center justify-center w-full sm:w-auto h-10">
                    <Users size={20} className="sm:mr-2" />
                    <span className="hidden sm:inline">{t('ADD_MULTIPLE_USERS')}</span>
                  </Button>
                </DialogTrigger>

                {isAddMultipleUsersModalOpen && <AddMultipleUsers onClose={() => setIsAddMultipleUsersModalOpen(false)} />}
              </Dialog>

              <Dialog open={isAddUserModalOpen} onOpenChange={setIsAddUserModalOpen}>
                <DialogTrigger asChild>
                  <Button variant="default" className="flex items-center justify-center w-full sm:w-auto h-10">
                    <Plus size={20} className="sm:mr-2" />
                    <span className="hidden sm:inline">{t('ADD_USER')}</span>
                  </Button>
                </DialogTrigger>

                {isAddUserModalOpen && <AddUser onClose={() => setIsAddUserModalOpen(false)} />}
              </Dialog>
            </div> :

            !isAddGroupButtonShow && (
              <div className="flex flex-col sm:flex-row gap-2">
                <Dialog open={isAddGroupModalOpen} onOpenChange={setIsAddGroupModalOpen}>
                  <DialogTrigger asChild>
                    <Button variant="default" className="flex items-center justify-center w-full sm:w-auto">
                      <Plus size={20} className="sm:mr-2" />
                      <span className="hidden sm:inline">{t('ADD_GROUP')}</span>
                    </Button>
                  </DialogTrigger>

                  <AddGroupModal reloadData={reloadDataGroupsData} isOpen={isAddGroupModalOpen} onClose={() => setIsAddGroupModalOpen(false)} />
                </Dialog>
              </div>
            )}
        </div>

        {/* Tabs with Profile page style */}
        <Tabs value={tabId}>
          <div className="my-3 sm:my-5 flex items-center rounded text-base">
            <TabsList className="border border-primary p-1 rounded-lg font-medium">
              <TabsTrigger onClick={() => handleTabChange('allUsers')} value="allUsers" className='data-[state=active]:bg-primary data-[state=active]:text-white text-primary-600 rounded-lg px-[12px] py-[6px]'>
                {t('ALL_USERS')}
              </TabsTrigger>
              <TabsTrigger onClick={() => handleTabChange('groups')} value="groups" className='data-[state=active]:bg-primary data-[state=active]:text-white text-primary-600 rounded-lg px-[12px] py-[6px]'>
                {t('GROUPS')}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* All Users Tab Content */}
          <TabsContent value="allUsers">
            <DataTable
              data={data?.data || []}
              columns={columns}
              onRowClick={handleViewDetails}
              isLoading={isLoading}
              error={error}
              toolbar={(table) => renderFilterToolbar(table)}
              pagination={{
                pageIndex: paginationState.pageIndex,
                pageSize: paginationState.pageSize,
                totalCount: paginationState.totalCount,
              }}
              onPaginationChange={handlePaginationChange}
              manualPagination={true}
              expandedContent={renderExpandedContent}
              mobileColumns={['fullName']}
              expandable={true}
            />
          </TabsContent>

          {/* Groups Tab Content */}
          <TabsContent value="groups">
            <GroupsDataTable
              isReload={isReloadGroupsData}
            />
          </TabsContent>
        </Tabs>
      </div>

      {!isMobile && (
        <UserDetails open={openSheet} onOpenChange={setOpenSheet} selectedUser={selectedUser} />
      )}

      <ConfirmationModal
        open={isResetPasswordModalOpen}
        onOpenChange={setIsResetPasswordModalOpen}
        title={t('RESET_PASSWORD_FOR_USER')}
        description={t('PASSWORD_RESET_SENT_EMAIL')}
        onConfirm={handleConfirmResetPassword}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />

      <ConfirmationModal
        open={isResendActivationModalOpen}
        onOpenChange={setIsResendActivationModalOpen}
        title={t('ACTIVATE_THIS_USER')}
        description={t('USER_ACTIVATION_RESTORED')}
        onConfirm={handleConfirmActivation}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />

      <ConfirmationModal
        open={isDeactivateUserModalOpen}
        onOpenChange={setIsDeactivateUserModalOpen}
        title={t('DEACTIVATE_USER')}
        description={t('DEACTIVATE_USER_CONFIRMATION')}
        onConfirm={handleConfirmDeactivation}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />

      <ConfirmationModal
        open={isCancelSubscriptionModalOpen}
        onOpenChange={setIsCancelSubscriptionModalOpen}
        title={t('CANCEL_SUBSCRIPTION')}
        description={t('CANCEL_SUBSCRIPTION_CONFIRMATION') || 'Are you sure you want to cancel this user\'s subscription? This action cannot be undone.'}
        onConfirm={handleConfirmCancelSubscription}
        confirmText={t('CONFIRM')}
        cancelText={t('CANCEL')}
      />

      <ChangePlanDialog
        open={isChangePlanModalOpen}
        onOpenChange={setIsChangePlanModalOpen}
        userId={selectedUser?.itemId || ''}
        userName={selectedUser ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.email : ''}
        currentPlan={selectedUser?.plan}
        currentPlanItem={selectedUser?.planItem}
      />
    </div>
  );
}